import { Avatar, Card, Flex, Progress, Typography } from 'antd';
import dayjs from 'dayjs';
import { ArrowUpRight, Undo2 } from 'lucide-react';

import { TextQuaternary, TextTertiary } from '../../../ui/components/text';
import { formatCredits } from '../shared/billing-format';

import { AutoRechargeSetting } from './AutoRechargeSetting';

import type { GetBillingOverviewResponseDto } from '../../../api/generated/model/getBillingOverviewResponseDto';

export type CreditsCardProps = {
  organizationId: string;
  overview: GetBillingOverviewResponseDto;
  portalLoading: boolean;
  onOpenPortal: () => void;
};

export const CreditsCard = ({
  organizationId,
  overview,
  portalLoading,
  onOpenPortal,
}: CreditsCardProps) => {
  const { plan, adjustmentRemaining } = overview.credits;
  const used = overview.usageThisPeriod.reduce((sum, item) => sum + item.credits, 0);
  const periodStart = overview.usagePeriod?.start;
  // The bar is the Monthly tile drawn: the share of this period's credits that is gone. What was
  // used in all, top-ups and support credits included, is the figure under it.
  const monthlyGone =
    plan && plan.included > 0 ? ((plan.included - plan.remaining) / plan.included) * 100 : 0;

  return (
    <Card style={{ borderRadius: 20, overflow: 'hidden' }} styles={{ body: { padding: 0 } }}>
      <Flex>
        {[
          {
            label: 'MONTHLY',
            testId: 'billing-monthly-credits',
            icon: <Undo2 size={11} />,
            iconBackground: 'var(--ant-color-fill-tertiary)',
            iconColor: 'var(--ant-color-text-secondary)',
            value: plan ? plan.remaining : 0,
            note: plan
              ? `of ${formatCredits(plan.included)}${plan.expiresAt ? ` · resets ${dayjs(plan.expiresAt).format('MMM D')}` : ''}`
              : 'arrive with your first paid invoice',
          },
          {
            label: 'TOP-UP',
            testId: 'billing-top-up-credits',
            icon: <ArrowUpRight size={11} />,
            iconBackground: 'var(--ant-color-primary)',
            iconColor: 'var(--ant-color-text-light-solid)',
            value: overview.credits.topUpRemaining,
            note: 'rolls over · never expires',
          },
        ].map((tile, index) => (
          <Flex
            key={tile.label}
            vertical
            gap={6}
            flex={1}
            style={{
              padding: '18px 18px 16px',
              borderRight: index === 0 ? '1px solid var(--ant-color-split)' : undefined,
            }}
          >
            <Flex align="center" gap={7}>
              <Avatar
                size={20}
                icon={tile.icon}
                style={{ background: tile.iconBackground, color: tile.iconColor }}
              />
              <TextQuaternary style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.05em' }}>
                {tile.label}
              </TextQuaternary>
            </Flex>
            <Typography.Text
              className="font-mono"
              strong
              data-testid={tile.testId}
              style={{ fontSize: 28 }}
            >
              {formatCredits(tile.value)}
            </Typography.Text>
            <TextTertiary style={{ fontSize: 12 }}>{tile.note}</TextTertiary>
          </Flex>
        ))}
      </Flex>

      <Flex vertical gap={8} style={{ padding: '0 18px 16px' }}>
        {Math.floor(adjustmentRemaining) > 0 && (
          <TextTertiary data-testid="billing-support-credits" style={{ fontSize: 12 }}>
            Plus{' '}
            <span className="font-mono" style={{ color: 'var(--ant-color-text-secondary)' }}>
              {formatCredits(adjustmentRemaining)}
            </span>{' '}
            credits added by K3X support.
          </TextTertiary>
        )}
        <Progress
          percent={monthlyGone}
          showInfo={false}
          size={['100%', 8]}
          strokeColor="var(--ant-purple-5)"
          railColor="var(--ant-color-fill-tertiary)"
        />
        <TextTertiary data-testid="billing-used" style={{ fontSize: 12 }}>
          <span className="font-mono" style={{ color: 'var(--ant-color-text-secondary)' }}>
            {Math.ceil(used).toLocaleString()}
          </span>{' '}
          used {periodStart ? `since ${dayjs(periodStart).format('MMM D')}` : 'this period'}.
          Monthly credits are spent first, so top-ups stay untouched until you need them.
        </TextTertiary>
      </Flex>

      <AutoRechargeSetting
        organizationId={organizationId}
        overview={overview}
        portalLoading={portalLoading}
        onOpenPortal={onOpenPortal}
      />
    </Card>
  );
};
